import React, { useEffect, useMemo, useState } from "react";
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Box, Typography, Avatar, IconButton, Grid,
    TextField, MenuItem, Switch, Button, Alert, CircularProgress, Divider,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

import { LOYALTY_API_BASE, SECTION_COLORS } from "../../constants";
import { loyaltyFetch } from "../../hooks/loyaltyFetch";
import { useRewardsUiCatalog } from "../../hooks/useRewardsUiCatalog";
import { loyaltyApiDetail } from "../../utils/loyaltyApiMessage";
import { fetchRewardForEdit, putRewardCouponTypes } from "../../utils/rewardCouponTypesApi";
import CouponTypesMultiSelect from "./CouponTypesMultiSelect";

const buildRewardUpdateErrorMessage = (status, detail) => {
    const fallback = detail || `Erreur HTTP ${status || "inconnu"}`;
    if (status !== 409) return fallback;

    const text = String(detail || "").toLowerCase();
    if (text.includes("catégorie") || text.includes("categorie") || text.includes("reward_category_id")) {
        return "La catégorie sélectionnée est invalide pour cette marque. Rechargez la liste des catégories et réessayez.";
    }
    if (text.includes("identique") || text.includes("existe déjà") || text.includes("existe deja")) {
        return "Une récompense similaire existe déjà. Modifiez le nom (ou la description) puis réessayez.";
    }
    return "Conflit de données côté serveur pendant la mise à jour. Vérifiez les valeurs saisies; si le problème persiste via API aussi, le schéma SQL de la table rewards doit être vérifié sur le serveur.";
};

const EditRewardDialog = ({ open, reward, onClose, onSaved }) => {
    const brandName = reward?.brand || "";
    const { updateCouponTypeField, couponTypesOptionsEndpoint } = useRewardsUiCatalog(brandName);

    const [form, setForm] = useState({ name: "", description: "", active: true, coupon_type_ids: [] });
    const [initialCouponTypeIds, setInitialCouponTypeIds] = useState([]);
    const [loadingReward, setLoadingReward] = useState(false);
    const [savingLinks, setSavingLinks] = useState(false);
    const [products, setProducts] = useState([]);
    const [loadingProducts, setLoadingProducts] = useState(false);
    const [productLines, setProductLines] = useState([{ product_id: "", quantity: 1 }]);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!open || !reward?.id || !brandName) {
            setInitialCouponTypeIds([]);
            return;
        }
        let cancelled = false;
        setLoadingReward(true);
        setError(null);
        fetchRewardForEdit(reward.id, brandName)
            .then((data) => {
                if (cancelled) return;
                const ids = Array.isArray(data.coupon_type_ids)
                    ? data.coupon_type_ids.map(String)
                    : [];
                setInitialCouponTypeIds(ids);
                setForm({
                    name: data.name || "",
                    description: data.description || "",
                    active: data.active ?? true,
                    coupon_type_ids: ids,
                });
                const existing = Array.isArray(data.products) ? data.products : [];
                setProductLines(
                    existing.length > 0
                        ? existing.map((p) => ({ product_id: String(p.product_id), quantity: Number(p.quantity || 1) }))
                        : [{ product_id: "", quantity: 1 }],
                );
            })
            .catch((err) => { if (!cancelled) setError(err.message); })
            .finally(() => { if (!cancelled) setLoadingReward(false); });
        return () => { cancelled = true; };
    }, [open, reward?.id, brandName]);

    useEffect(() => {
        if (!open || !brandName) return;
        let cancelled = false;
        setLoadingProducts(true);
        loyaltyFetch(`${LOYALTY_API_BASE}/admin/products?active=true&brand=${encodeURIComponent(brandName)}`, {
            headers: { "X-Brand": brandName },
        })
            .then(async (res) => {
                const data = await res.json().catch(() => null);
                if (!res.ok) throw new Error(data?.detail || `Erreur HTTP ${res.status}`);
                if (!cancelled) setProducts(Array.isArray(data) ? data : []);
            })
            .catch(() => { if (!cancelled) setProducts([]); })
            .finally(() => { if (!cancelled) setLoadingProducts(false); });
        return () => { cancelled = true; };
    }, [open, brandName]);

    const productById = useMemo(() => {
        const m = {};
        products.forEach((p) => { m[String(p.id)] = p; });
        return m;
    }, [products]);

    const selectedProductIds = useMemo(
        () => new Set(productLines.map((l) => String(l.product_id || "")).filter(Boolean)),
        [productLines],
    );

    const addProductLine = () => setProductLines((prev) => [...prev, { product_id: "", quantity: 1 }]);
    const removeProductLine = (idx) => setProductLines((prev) => prev.filter((_, i) => i !== idx));
    const updateProductLine = (idx, patch) =>
        setProductLines((prev) => prev.map((l, i) => (i === idx ? { ...l, ...patch } : l)));

    const handleChange = (field) => (e) =>
        setForm((p) => ({ ...p, [field]: e.target.value }));

    const couponTypesRequired = updateCouponTypeField?.required !== false;
    const isValid = form.name.trim()
        && (!couponTypesRequired || form.coupon_type_ids.length > 0);

    const linksDirty = JSON.stringify([...form.coupon_type_ids].sort())
        !== JSON.stringify([...initialCouponTypeIds].sort());

    const handleSaveLinksOnly = async () => {
        if (!reward?.id || !brandName) return;
        setSavingLinks(true);
        setError(null);
        try {
            await putRewardCouponTypes(reward.id, brandName, form.coupon_type_ids);
            setInitialCouponTypeIds([...form.coupon_type_ids]);
            onSaved();
        } catch (err) {
            setError(err.message);
        } finally {
            setSavingLinks(false);
        }
    };

    const handleSubmit = async () => {
        if (!isValid || !reward?.id || !brandName) return;
        setSubmitting(true);
        setError(null);
        try {
            const cleanedProducts = productLines
                .map((l) => ({
                    product_id: l.product_id || null,
                    quantity: Number(l.quantity || 1),
                }))
                .filter((l) => !!l.product_id);
            const body = {
                name: form.name.trim(),
                description: form.description.trim() || null,
                active: form.active,
                coupon_type_ids: form.coupon_type_ids,
                products: cleanedProducts.length > 0 ? cleanedProducts : [],
            };
            const res = await loyaltyFetch(`${LOYALTY_API_BASE}/rewards/${reward.id}`, {
                method: "PATCH",
                headers: { "Content-Type": "application/json", "X-Brand": brandName },
                body: JSON.stringify(body),
            });
            if (!res.ok) {
                const e = await res.json().catch(() => null);
                throw new Error(buildRewardUpdateErrorMessage(res.status, loyaltyApiDetail(e?.detail)));
            }
            setInitialCouponTypeIds([...form.coupon_type_ids]);
            onSaved();
            onClose();
        } catch (err) {
            setError(err.message);
        } finally {
            setSubmitting(false);
        }
    };

    if (!reward) return null;
    const busy = submitting || loadingReward || savingLinks;

    return (
        <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth
            PaperProps={{ sx: { borderRadius: 3 } }}>
            <DialogTitle sx={{ borderBottom: "2px solid #f1f3f5", px: 3, py: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Box display="flex" alignItems="center" gap={1}>
                    <Avatar sx={{ width: 32, height: 32, background: SECTION_COLORS.rewards.gradient }}>
                        <EditIcon sx={{ fontSize: 18 }} />
                    </Avatar>
                    <Box>
                        <Typography variant="h6" fontFamily="Montserrat, sans-serif" fontWeight={700} color="#1a1a2e" fontSize={16}>
                            Modifier la Récompense
                        </Typography>
                        <Typography fontSize={11} color="text.secondary">{brandName} • {reward.name}</Typography>
                    </Box>
                </Box>
                <IconButton onClick={onClose} size="small" disabled={busy}><CloseIcon /></IconButton>
            </DialogTitle>

            <DialogContent sx={{ pt: "24px !important", px: 3, pb: 2 }}>
                {loadingReward ? (
                    <Box display="flex" justifyContent="center" py={3}>
                        <CircularProgress size={28} />
                    </Box>
                ) : (
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <TextField fullWidth required label="Nom" value={form.name}
                                onChange={handleChange("name")} size="small" disabled={busy}
                                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }} />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField fullWidth label="Description" value={form.description}
                                onChange={handleChange("description")} size="small" multiline rows={2} disabled={busy}
                                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }} />
                        </Grid>
                        <Grid item xs={12}>
                            <CouponTypesMultiSelect
                                brandName={brandName}
                                value={form.coupon_type_ids}
                                onChange={(ids) => setForm((p) => ({ ...p, coupon_type_ids: ids }))}
                                disabled={busy}
                                label={updateCouponTypeField?.label || "Types de coupon liés"}
                                helperText={
                                    updateCouponTypeField?.helperText
                                    || updateCouponTypeField?.hint
                                    || "Modifiable en création et en édition"
                                }
                                required={couponTypesRequired}
                                optionsEndpoint={couponTypesOptionsEndpoint}
                            />
                            <Box display="flex" justifyContent="flex-end" mt={1}>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    disabled={busy || !linksDirty}
                                    onClick={handleSaveLinksOnly}
                                    sx={{ textTransform: "none", borderRadius: 2, fontWeight: 600 }}
                                >
                                    {savingLinks ? "Enregistrement…" : "Enregistrer les liens seuls (PUT)"}
                                </Button>
                            </Box>
                        </Grid>
                        <Grid item xs={12}>
                            <Box display="flex" alignItems="center" justifyContent="space-between" px={1.5} py={1}
                                sx={{ borderRadius: 2, bgcolor: "#fafbfd", border: "1px solid #e8ecf1" }}>
                                <Typography fontSize={13} fontWeight={600} fontFamily="Montserrat, sans-serif">Actif</Typography>
                                <Switch checked={form.active}
                                    onChange={() => setForm((p) => ({ ...p, active: !p.active }))}
                                    disabled={busy}
                                    sx={{
                                        "& .MuiSwitch-switchBase.Mui-checked": { color: SECTION_COLORS.rewards.main },
                                        "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { bgcolor: SECTION_COLORS.rewards.main },
                                    }} />
                            </Box>
                        </Grid>

                        <Grid item xs={12}>
                            <Box mt={1}>
                                <Divider sx={{ mb: 1.5 }} />
                                <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                                    <Typography fontWeight={800} fontFamily="Montserrat, sans-serif" color="#1a1a2e" fontSize={13}>
                                        Produits associés (optionnel)
                                    </Typography>
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        onClick={addProductLine}
                                        disabled={busy}
                                        sx={{ textTransform: "none", borderRadius: 2, fontWeight: 700 }}
                                    >
                                        Ajouter un produit
                                    </Button>
                                </Box>

                                {loadingProducts ? (
                                    <Box display="flex" alignItems="center" gap={1}>
                                        <CircularProgress size={16} />
                                        <Typography fontSize={12} color="text.secondary">Chargement des produits…</Typography>
                                    </Box>
                                ) : products.length === 0 ? (
                                    <Alert severity="info" sx={{ borderRadius: 2 }}>
                                        Aucun produit disponible. Créez d’abord un produit dans le catalogue.
                                    </Alert>
                                ) : (
                                    <Grid container spacing={1.5}>
                                        {productLines.map((line, idx) => {
                                            const pid = line.product_id ? String(line.product_id) : "";
                                            const prod = pid ? productById[pid] : null;
                                            return (
                                                <React.Fragment key={idx}>
                                                    <Grid item xs={12} sm={7}>
                                                        <TextField
                                                            fullWidth
                                                            select
                                                            label="Produit"
                                                            value={pid}
                                                            onChange={(e) => updateProductLine(idx, { product_id: e.target.value })}
                                                            size="small"
                                                            disabled={busy}
                                                            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                                                            helperText={prod?.points_value != null ? `Valeur: ${prod.points_value} point(s)` : " "}
                                                        >
                                                            <MenuItem value=""><em>Sélectionner…</em></MenuItem>
                                                            {products.map((p) => {
                                                                const idStr = String(p.id);
                                                                const taken = selectedProductIds.has(idStr) && idStr !== pid;
                                                                return (
                                                                    <MenuItem key={idStr} value={idStr} disabled={taken}>
                                                                        {p.name}{taken ? " (déjà ajouté)" : ""}
                                                                    </MenuItem>
                                                                );
                                                            })}
                                                        </TextField>
                                                    </Grid>
                                                    <Grid item xs={8} sm={3}>
                                                        <TextField
                                                            fullWidth
                                                            label="Quantité"
                                                            type="number"
                                                            value={line.quantity}
                                                            onChange={(e) => updateProductLine(idx, { quantity: e.target.value === "" ? "" : Number(e.target.value) })}
                                                            size="small"
                                                            disabled={busy}
                                                            InputProps={{ inputProps: { min: 1 } }}
                                                            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                                                        />
                                                    </Grid>
                                                    <Grid item xs={4} sm={2} display="flex" alignItems="center" justifyContent="flex-end">
                                                        <IconButton
                                                            onClick={() => removeProductLine(idx)}
                                                            disabled={busy || productLines.length === 1}
                                                            size="small"
                                                            sx={{ mt: { xs: 0.5, sm: 0 } }}
                                                        >
                                                            <DeleteOutlineIcon fontSize="small" />
                                                        </IconButton>
                                                    </Grid>
                                                </React.Fragment>
                                            );
                                        })}
                                    </Grid>
                                )}
                            </Box>
                        </Grid>
                    </Grid>
                )}
                {error && <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>{error}</Alert>}
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2, borderTop: "1px solid #e0e0e0", gap: 1 }}>
                <Button onClick={onClose} disabled={busy} sx={{ color: "text.secondary", textTransform: "none" }}>Annuler</Button>
                <Button variant="contained" onClick={handleSubmit} disabled={!isValid || busy}
                    startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                    sx={{ background: SECTION_COLORS.rewards.gradient, textTransform: "none", borderRadius: 2, fontWeight: 600, px: 3 }}>
                    {submitting ? "Enregistrement..." : "Enregistrer"}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default EditRewardDialog;
